/**
 * 放送地点に紐づく `contents[]` の編集 UI。
 *
 * - カテゴリのドロップダウン (自列車遅延 / 接続線遅延 / 任意テキスト) を切り替えると、
 *   そのカテゴリに必要な入力フィールドが動的に出現する。
 * - 各コンテンツは「↑」「↓」ボタンで並び替え、「× 削除」ボタンで除去できる。
 * - 「＋ コンテンツを追加」ボタンで末尾にデフォルト値のコンテンツを追加する。
 *
 * 状態管理は親コンポーネント (BroadcastPointForm) が保持し、本コンポーネントは
 * 受け取ったコンテンツ配列を表示・編集して onChange で返すだけのコントロールド
 * コンポーネントとして実装する。
 */
import type {
  BroadcastContent,
  BroadcastContentCategory,
  ConnectingDelayJudgementMode,
  ConnectingDirectionEntry,
  ConnectingLineDelayByStationProperties,
  ConnectingLineDelayProperties,
  ConnectingLineEntry,
  DelayBroadcastGranularity,
  DelayBroadcastPattern,
  FreeTextInputMode,
  FreeTextProperties,
  SelfTrainDelayProperties,
} from '../api/types';

import { useState } from 'react';

import type { RouteMaster } from '../data/routes';
import { sortStopsBySequence, type RouteStop } from '../data/routeStops';

import {
  CATEGORY_META,
  CONNECTING_GRANULARITY_DEFAULT_SSML,
  CONNECTING_SINGLE_SSML_DEFAULT,
  CONNECTING_STATION_GRANULARITY_DEFAULT_SSML,
  CONNECTING_STATION_INTRO_DEFAULT_SSML,
  CONNECTING_STATION_LINE_DEFAULT_SSML,
  DEFAULT_CONNECTING_DELAY_PATTERNS,
  DEFAULT_CONNECTING_STATION_DELAY_PATTERNS,
  DELAY_BROADCAST_GRANULARITIES,
  GRANULARITY_DEFAULT_SSML,
  cloneContent,
  createDefaultConnectingStationDirection,
  createDefaultConnectingStationLine,
  createDefaultContent,
  getCategoryMeta,
  moveContent,
} from '../utils/broadcastContents';

/**
 * 任意テキスト / SSML の音声プレビュー処理。
 *
 * - `text` または `ssml` のいずれかを受け取り、再生可能な object URL (blob:) を返す。
 * - `text` を SSML として扱う場合は `isSsml: true` を指定する。
 * - 省略時はプレビュー UI を無効化する。
 */
export type PreviewSsmlHandler = (input: {
  text?: string;
  ssml?: string;
  isSsml?: boolean;
}) => Promise<string>;

interface BroadcastContentsEditorProps {
  contents: BroadcastContent[];
  onChange: (next: BroadcastContent[]) => void;
  /**
   * 接続線遅延コンテンツの route_id ドロップダウン用の線区マスタ。
   * 省略時は空配列として扱い、未登録 route_id のみ表示される。
   */
  routes?: RouteMaster[];
  /**
   * 接続線遅延コンテンツの「遅延判定開始駅 / 終了駅」ドロップダウン用の駅順マスタ。
   * route_id をキーに、その路線の駅一覧 (stop_sequence 昇順) を持つ。
   * 親 (BroadcastPointsPage) が ControlPanel API 経由で事前取得して渡す。省略時は空。
   */
  routeStops?: Record<string, RouteStop[]>;
  /**
   * 親フォーム (BroadcastPointForm) が編集している放送地点の「自路線」の route_id。
   *
   * 「接続線遅延 (乗換駅別)」コンテンツの乗換駅ドロップダウンで、`routeStops` の中から
   * この route_id に対応する駅一覧のみを候補として表示するために使う。
   *
   * 親から渡される `routeStops` は複数 route_id ぶんが 1 つの Record にまとまっている
   * (BroadcastPointsPage が全路線ぶん事前取得しているため) ので、`parentRouteId` が
   * 与えられない場合は「どの路線が親か」を特定できず、乗換駅ドロップダウンは空になる
   * (以前は Record の先頭キーを暗黙採用していたが、Record の並び順は保証されず
   *  意図しない路線の駅が表示される不具合が発生していたため、明示指定を必須化する)。
   */
  parentRouteId?: string;
  /**
   * 任意テキスト (free_text) コンテンツの音声ファイルアップロード処理。
   * MP3 File を受け取り、保存された audio_key を返す。省略時はアップロード UI を無効化する。
   */
  onUploadAudio?: (file: File) => Promise<string>;
  /**
   * SSML / 任意テキストの音声プレビュー処理。
   * 省略時はプレビュー UI を無効化する。
   */
  onPreviewSsml?: PreviewSsmlHandler;
}

export function BroadcastContentsEditor({
  contents,
  onChange,
  routes = [],
  routeStops = {},
  parentRouteId,
  onUploadAudio,
  onPreviewSsml,
}: BroadcastContentsEditorProps): React.JSX.Element {
  const updateAt = (index: number, content: BroadcastContent): void => {
    const next = contents.slice();
    next[index] = content;
    onChange(next);
  };

  const removeAt = (index: number): void => {
    const next = contents.slice();
    next.splice(index, 1);
    onChange(next);
  };

  const moveUp = (index: number): void => {
    onChange(moveContent(contents, index, index - 1));
  };
  const moveDown = (index: number): void => {
    onChange(moveContent(contents, index, index + 1));
  };

  const handleAdd = (): void => {
    onChange([...contents, createDefaultContent('self_train_delay')]);
  };

  const handleCategoryChange = (index: number, category: BroadcastContentCategory): void => {
    // カテゴリを変えたら properties は新カテゴリのデフォルト値で初期化する
    updateAt(index, createDefaultContent(category));
  };

  return (
    <div className="contents-editor">
      <div className="contents-editor-header">
        <strong>コンテンツ ({contents.length} 件)</strong>
        <button
          type="button"
          data-testid="add-content-button"
          aria-label="コンテンツを追加"
          onClick={handleAdd}
        >
          ＋ コンテンツを追加
        </button>
      </div>

      {contents.length === 0 ? (
        <div className="empty-state" style={{ padding: '12px 8px' }}>
          コンテンツは設定されていません。「＋ コンテンツを追加」から作成してください。
        </div>
      ) : null}

      <ol className="contents-list">
        {contents.map((content, index) => (
          <li key={index} className="contents-item" data-testid={`content-row-${index}`}>
            <div className="contents-item-header">
              <span className="contents-item-index">#{index + 1}</span>
              {/*
               * カテゴリ <select>。E2E / AI エージェントが画面トップの一覧フィルタ
               * (id="content-filter") と取り違えやすいので、明示的な data-testid を
               * 付与してモーダル内のコンテンツカテゴリ <select> を一意に取得できるようにする。
               * testid は 0 始まりの index を用いる (画面表示の "#i" は 1 始まりだが、
               * 他の content-row-{index} と同じ流儀にする)。
               */}
              <select
                aria-label={`コンテンツ#${index + 1} カテゴリ`}
                data-testid={`content-category-select-${index}`}
                value={content.category}
                onChange={(e) =>
                  handleCategoryChange(index, e.target.value as BroadcastContentCategory)
                }
              >
                {CATEGORY_META.map((m) => (
                  <option key={m.category} value={m.category}>
                    {m.label}
                  </option>
                ))}
              </select>
              <div className="contents-item-actions">
                <button
                  type="button"
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  aria-label={`コンテンツ#${index + 1} を上へ`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveDown(index)}
                  disabled={index === contents.length - 1}
                  aria-label={`コンテンツ#${index + 1} を下へ`}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="danger"
                  onClick={() => removeAt(index)}
                  aria-label={`コンテンツ#${index + 1} を削除`}
                >
                  × 削除
                </button>
              </div>
            </div>
            <div className="contents-item-description">
              {getCategoryMeta(content.category)?.description}
            </div>
            <ContentPropertyFields
              content={content}
              routes={routes}
              routeStops={routeStops}
              parentRouteId={parentRouteId}
              onUploadAudio={onUploadAudio}
              onPreviewSsml={onPreviewSsml}
              onChange={(c) => updateAt(index, c)}
            />
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * 接続線遅延 (乗換駅ベース / part2) の 1 接続線ブロック内の「遅延放送パターン表」。
 *
 * 自列車遅延の `SelfTrainDelayFields` / part1 の `ConnectingLineDelayPatternsTable` と
 * 同じ `DelayPatternRow` を再利用し、遅れ分数の幅ごとに 1 行ずつ
 * 「遅れ分数 / 放送粒度 / 放送パターン (方面部分文SSML) / 行操作 (＋−)」を表示する。
 *
 * ここで編集する `ssml_template` は **方面部分文** (`{directions_text}` に流し込まれる素材)
 * である点が part1 の transfer_aware 用テーブルと違う。placeholder / 凡例も part2 用に切り替える。
 */
function ConnectingStationLineDelayPatternsTable({
  lineIndex,
  patterns,
  onChange,
}: {
  /** 親接続線ブロックの index (data-testid 用) */
  lineIndex: number;
  patterns: DelayBroadcastPattern[];
  onChange: (nextPatterns: DelayBroadcastPattern[]) => void;
}): React.JSX.Element {
  // 空配列の場合は最低 1 行を表示 (part1 と同じポリシー)。
  const rows: DelayBroadcastPattern[] =
    patterns.length > 0
      ? patterns
      : [createDefaultPattern(CONNECTING_STATION_GRANULARITY_DEFAULT_SSML)];

  const updatePattern = (index: number, patch: Partial<DelayBroadcastPattern>): void => {
    const next = rows.slice();
    const current = next[index]!;
    // 粒度が変わったら接続線 part2 用のデフォルト方面部分文に上書きする
    // (ユーザ要望: 「粒度を変えたら元々のデフォルト値を再設定して」)
    if (patch.granularity && patch.granularity !== current.granularity) {
      next[index] = {
        ...current,
        ...patch,
        ssml_template: CONNECTING_STATION_GRANULARITY_DEFAULT_SSML[patch.granularity],
      };
    } else {
      next[index] = { ...current, ...patch };
    }
    onChange(next);
  };

  const addPatternAfter = (index: number): void => {
    const next = rows.slice();
    next.splice(index + 1, 0, createDefaultPattern(CONNECTING_STATION_GRANULARITY_DEFAULT_SSML));
    onChange(next);
  };

  const removePattern = (index: number): void => {
    if (rows.length <= 1) return;
    const next = rows.slice();
    next.splice(index, 1);
    onChange(next);
  };

  return (
    <div
      className="connecting-station-line-delay-patterns"
      data-testid={`connecting-station-line-delay-patterns-${lineIndex}`}
      style={{ marginTop: 8 }}
    >
      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>遅延放送パターン (方面部分文 SSML)</div>
      <table className="delay-patterns-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '4px 6px', whiteSpace: 'nowrap' }}>
              遅れ分数
            </th>
            <th style={{ textAlign: 'left', padding: '4px 6px', whiteSpace: 'nowrap' }}>
              放送粒度
            </th>
            <th style={{ textAlign: 'left', padding: '4px 6px' }}>
              放送パターン (方面部分文 SSML)
            </th>
            <th
              aria-label="行操作"
              style={{ textAlign: 'center', padding: '4px 6px', whiteSpace: 'nowrap' }}
            />
          </tr>
        </thead>
        <tbody>
          {rows.map((pattern, i) => (
            <DelayPatternRow
              key={i}
              index={i}
              pattern={pattern}
              canRemove={rows.length > 1}
              ssmlTestIdPrefix={`connecting-station-line-${lineIndex}-direction-ssml`}
              placeholderTemplates={CONNECTING_STATION_GRANULARITY_DEFAULT_SSML}
              onUpdate={(patch) => updatePattern(i, patch)}
              onAdd={() => addPatternAfter(i)}
              onRemove={() => removePattern(i)}
            />
          ))}
        </tbody>
      </table>
      <p className="hint" style={{ margin: '8px 0 4px' }}>
        遅れ分数の幅ごとに「方面部分文 SSML」を設定します。この文言が各方面ごとに展開され、
        読点で連結されて接続線ブロックの <code>{'{directions_text}'}</code> に埋め込まれます。
      </p>
      <PlaceholderLegend
        testId={`connecting-station-line-direction-hint-${lineIndex}`}
        items={CONNECTING_STATION_DIRECTION_PLACEHOLDERS}
      />
    </div>
  );
}

/**
 * 接続線遅延 (乗換駅ベース / part2) の 1 方面ぶんのブロック。
 * `ConnectingDirectionsSection` の 1 方面を編集する。
 *
 * 判定モードによる項目表示切替:
 *   - whole_line       : direction_id (判定用) / 区間 (start/end_stop_id) 非表示。
 *                        direction_name (案内文面用) のみ入力。
 *   - section_scheduled: direction_id + 区間 (start/end_stop_id) + direction_name。
 *   - transfer_aware   : direction_id + direction_name (区間は非表示)。
 */
function ConnectingDirectionBlock({
  lineIndex,
  directionIndex,
  direction,
  routeId,
  judgementMode,
  routes,
  routeStops,
  canRemove,
  isFirst,
  isLast,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  lineIndex: number;
  directionIndex: number;
  direction: ConnectingDirectionEntry;
  routeId: string;
  judgementMode: ConnectingDelayJudgementMode;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  canRemove: boolean;
  isFirst: boolean;
  isLast: boolean;
  onChange: (next: ConnectingDirectionEntry) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}): React.JSX.Element {
  const directionId = direction.direction_id ?? '';
  const directionName = direction.direction_name ?? '';
  const startStopId = direction.start_stop_id ?? '';
  const endStopId = direction.end_stop_id ?? '';

  const isWholeLineMode = judgementMode === 'whole_line';
  const isSectionScheduledMode = judgementMode === 'section_scheduled';

  // route_id に紐づく方面選択肢を生成 (part1 と同じ流儀)。
  const currentRoute = routes.find((r) => r.route_id === routeId);
  const directionOptions = (currentRoute?.directions ?? []).map((d, i) => ({
    value: typeof d.direction_id === 'string' && d.direction_id !== '' ? d.direction_id : String(i),
    label: d.direction_name,
  }));
  const isUnknownDirection =
    directionId !== '' && !directionOptions.some((o) => o.value === directionId);

  // 区間駅 (start/end_stop_id) の選択肢は route_id の駅一覧から。
  const stops = sortStopsBySequence(routeId ? routeStops[routeId] : []);

  /** 方面を patch で更新 */
  const emit = (patch: Partial<ConnectingDirectionEntry>): void => {
    onChange({ ...direction, ...patch });
  };

  const testIdSuffix = `${lineIndex}-${directionIndex}`;

  return (
    <div
      className="connecting-direction-block"
      data-testid={`connecting-direction-block-${testIdSuffix}`}
    >
      {/* ヘッダ: ラベル + 操作ボタン (↑↓ / − 削除) */}
      <div
        className="connecting-direction-block-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 4,
          flexWrap: 'wrap',
        }}
      >
        <strong>方面 #{directionIndex + 1}</strong>
        <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
          <button
            type="button"
            aria-label={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} を上へ`}
            data-testid={`connecting-direction-move-up-${testIdSuffix}`}
            disabled={isFirst}
            onClick={onMoveUp}
          >
            ↑
          </button>
          <button
            type="button"
            aria-label={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} を下へ`}
            data-testid={`connecting-direction-move-down-${testIdSuffix}`}
            disabled={isLast}
            onClick={onMoveDown}
          >
            ↓
          </button>
          {canRemove ? (
            <button
              type="button"
              className="danger"
              aria-label={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} を削除`}
              data-testid={`connecting-direction-remove-${testIdSuffix}`}
              onClick={onRemove}
            >
              − 削除
            </button>
          ) : null}
        </div>
      </div>

      {/* 判定用の方面 direction_id (whole_line では非表示) */}
      {!isWholeLineMode ? (
        <label>
          <span>方面 (判定用・任意)</span>
          <select
            aria-label={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} 判定用`}
            data-testid={`connecting-direction-id-${testIdSuffix}`}
            value={directionId}
            disabled={!routeId}
            onChange={(e) => emit({ direction_id: e.target.value })}
          >
            <option value="">(指定なし)</option>
            {directionOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            {isUnknownDirection ? (
              <option value={directionId}>{directionId} (マスタ未登録)</option>
            ) : null}
          </select>
        </label>
      ) : null}

      {/* 案内文面用の direction_name (必須) */}
      <label>
        <span className="required">方面 (案内文面用)</span>
        <div className="direction-name-input-row">
          <input
            type="text"
            aria-label={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} 案内文面`}
            data-testid={`connecting-direction-name-${testIdSuffix}`}
            required
            value={directionName}
            placeholder="例: 新宿"
            onChange={(e) => emit({ direction_name: e.target.value })}
          />
          <span
            className="direction-name-suffix"
            data-testid={`connecting-direction-name-suffix-${testIdSuffix}`}
            aria-hidden="true"
          >
            方面
          </span>
        </div>
      </label>
      <p className="hint" style={{ margin: '4px 0' }}>
        放送文の <code>{'{direction_name}'}</code> プレースホルダに展開されます (例: 「新宿」→
        「新宿方面は5分遅れ」)。必ず入力してください。
      </p>

      {/* section_scheduled モードでは区間 (開始/終了駅) を方面ごとに指定 */}
      {isSectionScheduledMode ? (
        <>
          <div style={{ display: 'flex', gap: 8 }}>
            <label style={{ flex: 1 }}>
              <span className="required">遅延判定開始駅</span>
              <StopSelect
                ariaLabel={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} 開始駅`}
                testId={`connecting-direction-start-stop-${testIdSuffix}`}
                value={startStopId}
                stops={stops}
                disabled={!routeId}
                onChange={(stopId) => emit({ start_stop_id: stopId })}
              />
            </label>
            <label style={{ flex: 1 }}>
              <span className="required">遅延判定終了駅</span>
              <StopSelect
                ariaLabel={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} 終了駅`}
                testId={`connecting-direction-end-stop-${testIdSuffix}`}
                value={endStopId}
                stops={stops}
                disabled={!routeId}
                onChange={(stopId) => emit({ end_stop_id: stopId })}
              />
            </label>
          </div>
          <p className="hint" style={{ margin: '4px 0' }}>
            方面ごとに区間を指定します。区間内に定刻在線している便の遅延で判定します。
          </p>
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// カテゴリ別プロパティフィールド
// ---------------------------------------------------------------------------

interface ContentPropertyFieldsProps {
  content: BroadcastContent;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  /**
   * 親フォーム側で編集中の放送地点の「自路線」の route_id。
   * `connecting_line_delay_by_station` の乗換駅ドロップダウンで、`routeStops` を
   * 自路線に絞り込むために使う。詳細は `BroadcastContentsEditorProps.parentRouteId` を参照。
   */
  parentRouteId?: string;
  onChange: (content: BroadcastContent) => void;
  onUploadAudio?: (file: File) => Promise<string>;
  onPreviewSsml?: PreviewSsmlHandler;
}

function ContentPropertyFields({
  content,
  routes,
  routeStops,
  parentRouteId,
  onChange,
  onUploadAudio,
  onPreviewSsml,
}: ContentPropertyFieldsProps): React.JSX.Element {
  switch (content.category) {
    case 'self_train_delay':
      return (
        <SelfTrainDelayFields
          props={content.properties as Partial<SelfTrainDelayProperties>}
          onChange={(props) => onChange({ ...cloneContent(content), properties: props })}
        />
      );
    case 'connecting_line_delay':
      return (
        <ConnectingLineDelayFields
          props={content.properties as Partial<ConnectingLineDelayProperties>}
          routes={routes}
          routeStops={routeStops}
          onChange={(props) => onChange({ ...cloneContent(content), properties: props })}
        />
      );
    case 'connecting_line_delay_by_station':
      return (
        <ConnectingLineDelayByStationFields
          props={content.properties as Partial<ConnectingLineDelayByStationProperties>}
          routes={routes}
          routeStops={routeStops}
          parentRouteId={parentRouteId}
          onChange={(props) => onChange({ ...cloneContent(content), properties: props })}
        />
      );

    case 'free_text':
      return (
        <FreeTextFields
          props={content.properties as Partial<FreeTextProperties>}
          onUploadAudio={onUploadAudio}
          onPreviewSsml={onPreviewSsml}
          onChange={(props) => onChange({ ...cloneContent(content), properties: props })}
        />
      );

    default:
      return (
        <div className="error-message">
          未対応のカテゴリ: {(content as { category: string }).category}
        </div>
      );
  }
}

/**
 * 自列車遅延パターンの新規行デフォルト値を生成する (「＋」追加時に使用)。
 *
 * SSML 入力欄に粒度別デフォルト文 (`GRANULARITY_DEFAULT_SSML`) を **実値として**
 * 入れる方針に変更したため、新規行追加時にも対応する粒度のデフォルト文を
 * `ssml_template` に詰める。`templates` を切り替えれば接続線 transfer_aware 用の
 * デフォルト文 (`CONNECTING_GRANULARITY_DEFAULT_SSML`) にも適用できる。
 */
function createDefaultPattern(
  templates: Record<DelayBroadcastGranularity, string> = GRANULARITY_DEFAULT_SSML,
): DelayBroadcastPattern {
  return {
    delay_minutes_from: 0,
    granularity: '1分単位',
    ssml_template: templates['1分単位'],
  };
}

/**
 * テンプレート凡例の中で「クリックでクリップボードコピーできる」プレースホルダ表示。
 *
 * - `<code>` 風の見た目を維持しつつ `<button>` 要素として描画する。
 * - クリックすると `navigator.clipboard.writeText` でトークン文字列をコピーし、
 *   一時的に「コピーしました」を吹き出しで表示する。
 * - クリップボード API が使えない環境 (古いブラウザ / セキュア コンテキスト外) では
 *   そっとフォールバックして何もしない (テキスト選択は可能)。
 *
 * ユーザは説明文を「読む」というより、SSML 入力欄に `{delay_minutes_1}` 等を
 * コピペして書き始める用途で凡例を使うため、クリック 1 回でコピーできる UI を採用する。
 */
function CopyableToken({ token }: { token: string }): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  const handleCopy = (): void => {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(token).then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1200);
      });
    }
  };
  return (
    <button
      type="button"
      className="copyable-token"
      onClick={handleCopy}
      title={`${token} をコピー`}
      aria-label={`${token} をクリップボードにコピー`}
      style={{
        font: 'inherit',
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
        background: '#f4f6f8',
        border: '1px solid #d0d7de',
        borderRadius: 4,
        padding: '1px 6px',
        cursor: 'pointer',
        color: '#24292f',
      }}
    >
      <code style={{ background: 'transparent', padding: 0 }}>{token}</code>
      {copied ? (
        <span
          aria-live="polite"
          style={{
            marginLeft: 4,
            fontSize: '0.85em',
            color: '#0969da',
          }}
        >
          ✓ コピー済み
        </span>
      ) : null}
    </button>
  );
}

/**
 * プレースホルダ凡例で使う「トークンと説明」の対。
 * UI 上では `{token}` (コピー可能) と説明文を <li> として並べる。
 */
interface PlaceholderItem {
  token: string;
  description: string;
}

/**
 * プレースホルダ凡例の共通 UI。
 * トークンと簡易説明をリストで並べ、各トークンをクリックでクリップボードコピーできる。
 */
function PlaceholderLegend({
  items,
  testId,
  note,
}: {
  items: PlaceholderItem[];
  testId?: string;
  /** 凡例の下に追加で表示する補足文 (任意) */
  note?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="placeholder-legend hint" data-testid={testId} style={{ margin: '4px 0' }}>
      <div style={{ marginBottom: 4 }}>利用できるプレースホルダ (クリックでコピー):</div>
      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: '4px 8px',
          alignItems: 'baseline',
        }}
      >
        {items.map((item) => (
          <li key={item.token} style={{ display: 'contents' }}>
            <CopyableToken token={item.token} />
            <span>{item.description}</span>
          </li>
        ))}
      </ul>
      {note ? <div style={{ marginTop: 6 }}>{note}</div> : null}
    </div>
  );
}

function SelfTrainDelayFields({
  props,
  onChange,
}: {
  props: Partial<SelfTrainDelayProperties>;
  onChange: (props: SelfTrainDelayProperties) => void;
}): React.JSX.Element {
  // 放送粒度毎の行は固定ではなく、ユーザーが「＋」「−」で追加・削除できる。
  // 既存データをそのまま行として表示し、空の場合は最低 1 行を表示する。
  const existingPatterns: DelayBroadcastPattern[] = Array.isArray(props.delay_patterns)
    ? props.delay_patterns
    : [];
  const patterns: DelayBroadcastPattern[] =
    existingPatterns.length > 0 ? existingPatterns : [createDefaultPattern()];

  const updatePattern = (index: number, patch: Partial<DelayBroadcastPattern>): void => {
    const next = patterns.slice();
    const current = next[index]!;
    // 粒度プルダウンが変更された場合は、対応する粒度のデフォルト SSML 文言を
    // ssml_template に再設定する (ユーザ要望: 「粒度を変えたら元々のデフォルト値を
    // 再設定して」)。ユーザがカスタム編集していた場合も上書きされるが、
    // SSML 欄をクリアすればゼロベース入力に戻せるので問題ない。
    if (patch.granularity && patch.granularity !== current.granularity) {
      next[index] = {
        ...current,
        ...patch,
        ssml_template: GRANULARITY_DEFAULT_SSML[patch.granularity],
      };
    } else {
      next[index] = { ...current, ...patch };
    }
    onChange({ delay_patterns: next });
  };

  /** index の直後に新しい行を挿入する */
  const addPatternAfter = (index: number): void => {
    const next = patterns.slice();
    next.splice(index + 1, 0, createDefaultPattern());
    onChange({ delay_patterns: next });
  };

  /** index の行を削除する (最後の 1 行は削除しない) */
  const removePattern = (index: number): void => {
    if (patterns.length <= 1) return;
    const next = patterns.slice();
    next.splice(index, 1);
    onChange({ delay_patterns: next });
  };

  return (
    <div className="contents-fields">
      <table className="delay-patterns-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '4px 6px', whiteSpace: 'nowrap' }}>
              遅れ分数
            </th>
            <th style={{ textAlign: 'left', padding: '4px 6px', whiteSpace: 'nowrap' }}>
              放送粒度
            </th>
            <th style={{ textAlign: 'left', padding: '4px 6px' }}>
              放送パターン (SSMLテンプレート)
            </th>
            <th
              aria-label="行操作"
              style={{ textAlign: 'center', padding: '4px 6px', whiteSpace: 'nowrap' }}
            />
          </tr>
        </thead>
        <tbody>
          {patterns.map((pattern, i) => (
            <DelayPatternRow
              key={i}
              index={i}
              pattern={pattern}
              canRemove={patterns.length > 1}
              onUpdate={(patch) => updatePattern(i, patch)}
              onAdd={() => addPatternAfter(i)}
              onRemove={() => removePattern(i)}
            />
          ))}
        </tbody>
      </table>

      <p className="hint" style={{ margin: '8px 0 4px' }}>
        各行の「遅れ分数」の範囲に隙間が生じないよう注意して設定してください。
        各行の「＋」で行を追加、「−」で行を削除できます。 SSML
        欄は必須項目です。各放送粒度のデフォルト文言があらかじめ入力されているので、必要に応じて書き換えてください。
      </p>
      {/*
        放送パターン (SSML テンプレート) で使えるプレースホルダの凡例。
        ユーザは説明文を「読む」というより、SSML 欄に `{delay_minutes_1}` 等を
        コピペして書き始める用途で凡例を使うので、トークンを縦に並べて
        クリック 1 回でクリップボードにコピーできる UI にする。
      */}
      <PlaceholderLegend
        testId="self-train-delay-route-hint"
        items={SELF_TRAIN_DELAY_PLACEHOLDERS}
      />
    </div>
  );
}

/**
 * 自列車遅延の「放送パターン (SSMLテンプレート)」入力欄で使えるプレースホルダ一覧。
 * 凡例 UI (`PlaceholderLegend`) に渡してトークン + 簡易説明として表示する。
 */
const SELF_TRAIN_DELAY_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{delay_minutes_1}', description: '1分単位で切り捨てた遅延分数' },
  { token: '{delay_minutes_5}', description: '5分単位で切り捨てた遅延分数' },
  { token: '{delay_minutes_10}', description: '10分単位で切り捨てた遅延分数' },
  { token: '{delay_minutes}', description: '切り上げ・最低1分の遅延分数' },
  { token: '{delay_seconds}', description: '遅延秒数' },
  { token: '{delay}', description: '{delay_seconds} と同じ意味' },
  { token: '{route_name}', description: '自路線の一般的な線名 (未登録時は {route_id} の値)' },
  { token: '{route_id}', description: 'ATOS 線名コード (路線ID)' },
  { token: '{route}', description: '{route_name} と同じ意味' },
];

function DelayPatternRow({
  index,
  pattern,
  canRemove,
  ssmlTestIdPrefix = 'self-train-delay-ssml-template',
  placeholderTemplates = GRANULARITY_DEFAULT_SSML,
  onUpdate,
  onAdd,
  onRemove,
}: {
  index: number;
  pattern: DelayBroadcastPattern;
  /** 削除可能か (最後の 1 行のときは false。false のとき「−」ボタンを表示しない) */
  canRemove: boolean;
  /**
   * SSML テンプレート <textarea> に付与する data-testid のプレフィックス。
   * 自列車遅延と接続線遅延 (transfer_aware) の両方で同じ行コンポーネントを再利用するため、
   * 呼び出し元で区別できるように `<prefix>-<index>` の testid が割り当てられる。
   * デフォルトは自列車遅延の従来 testid (`self-train-delay-ssml-template-{index}`) を維持する。
   */
  ssmlTestIdPrefix?: string;
  /**
   * 放送粒度ごとのデフォルト SSML 文言 (textarea の placeholder に使われる)。
   * 自列車遅延と接続線遅延 (transfer_aware) で文面が異なるため、呼び出し元から
   * 注入できるようにする。デフォルトは自列車遅延の文言
   * (`GRANULARITY_DEFAULT_SSML` / Lambda の `GRANULARITY_DEFAULT_TEMPLATE` と一致)。
   * 接続線遅延の表形式エディタからは `CONNECTING_GRANULARITY_DEFAULT_SSML` を渡し、
   * Lambda の `CONNECTING_GRANULARITY_DEFAULT_TEMPLATE` と一致した
   * 「<駅名>駅からお乗換の<線名>... 現在 N 分ほど遅れています」系の文面を表示する。
   */
  placeholderTemplates?: Record<DelayBroadcastGranularity, string>;
  onUpdate: (patch: Partial<DelayBroadcastPattern>) => void;
  onAdd: () => void;
  onRemove: () => void;
}): React.JSX.Element {
  const granularity = pattern.granularity as DelayBroadcastGranularity;

  return (
    <tr style={{ verticalAlign: 'top', borderTop: '1px solid #ddd' }}>
      {/* 遅れ分数 */}
      <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap' }}>
          <input
            type="number"
            min={0}
            step={1}
            style={{ width: 90 }}
            aria-label={`パターン${index + 1} 遅れ開始分数`}
            value={pattern.delay_minutes_from}
            onChange={(e) =>
              onUpdate({ delay_minutes_from: Math.max(0, Math.floor(Number(e.target.value))) })
            }
          />
          <span>分以上〜</span>
          <input
            type="number"
            min={0}
            step={1}
            style={{ width: 90 }}
            aria-label={`パターン${index + 1} 遅れ終了分数`}
            value={pattern.delay_minutes_to ?? ''}
            placeholder="上限なし"
            onChange={(e) => {
              const val = e.target.value;
              onUpdate({
                delay_minutes_to: val === '' ? undefined : Math.max(0, Math.floor(Number(val))),
              });
            }}
          />
          <span>分未満の遅れ</span>
        </div>
      </td>

      {/* 放送粒度 */}
      <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>
        <select
          aria-label={`パターン${index + 1} 放送粒度`}
          value={granularity}
          onChange={(e) => onUpdate({ granularity: e.target.value as DelayBroadcastGranularity })}
        >
          {DELAY_BROADCAST_GRANULARITIES.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
      </td>

      {/* 放送パターン (SSMLテンプレート) */}
      <td style={{ padding: '6px', width: '100%' }}>
        {/*
         * data-testid を付与する理由:
         * placeholder には粒度別のデフォルト SSML 文 (例: "<speak>...</speak>") が
         * 動的に入るため、page-summary が拾うラベル (placeholder 優先) が長文化し、
         * AI エージェントが基本情報の「説明」欄 (data-testid=description-input) と
         * 取り違える事象が報告された (Claude が長考の末「{誤った action} 申し訳ありません
         * {正しい action}」と自己訂正レスポンスを返す原因になる)。
         * 明示的な data-testid を付けることで page-summary でも安定したラベル
         * (`self-train-delay-ssml-template-{index}`) として現れ、E2E シナリオからも
         * data-testid 単独で一意に拾えるようにする。
         */}
        <textarea
          rows={2}
          style={{ width: '100%', boxSizing: 'border-box' }}
          aria-label={`パターン${index + 1} 放送パターン`}
          data-testid={`${ssmlTestIdPrefix}-${index}`}
          // ユーザ要望: 各 SSML 入力欄は必須項目にする。
          // (デフォルト文言を初期値としてプリセットしているため、削除→ゼロベース入力する
          //  場合も保存ボタンを押した瞬間にバリデーションで気付ける)
          required
          value={pattern.ssml_template ?? ''}
          placeholder={placeholderTemplates[granularity]}
          onChange={(e) =>
            // 空文字入力 (ユーザがクリアしてゼロベース入力に入った状態) もそのまま反映する。
            // ※ バリデーション (`validateContent`) は空文字をエラーとして検出する。
            onUpdate({ ssml_template: e.target.value })
          }
        />
      </td>

      {/* 操作 (行の追加・削除) */}
      <td style={{ padding: '6px', whiteSpace: 'nowrap', textAlign: 'center' }}>
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
          <button
            type="button"
            className="delay-pattern-add"
            aria-label={`パターン${index + 1} の後に行を追加`}
            title="行を追加"
            onClick={onAdd}
          >
            ＋
          </button>
          {canRemove ? (
            <button
              type="button"
              className="delay-pattern-remove danger"
              aria-label={`パターン${index + 1} を削除`}
              title="行を削除"
              onClick={onRemove}
            >
              −
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

/** 接続線遅延の判定モード選択肢 (フロント表示ラベル) */
const CONNECTING_DELAY_MODE_META: ReadonlyArray<{
  mode: ConnectingDelayJudgementMode;
  label: string;
}> = [
  { mode: 'whole_line', label: '接続線全体の遅延' },
  { mode: 'section_scheduled', label: '区間・在線便の遅延' },
  { mode: 'transfer_aware', label: '乗り換え列車の遅延' },
];

function ConnectingLineDelayFields({
  props,
  routes,
  routeStops,
  onChange,
}: {
  props: Partial<ConnectingLineDelayProperties>;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  onChange: (props: ConnectingLineDelayProperties) => void;
}): React.JSX.Element {
  const threshold = props.delay_threshold_seconds ?? 0;
  const currentRouteId = props.route_id ?? '';
  // 判定モード。未指定 (既存データ) は接続線全体 (whole_line) として表示する。
  const judgementMode: ConnectingDelayJudgementMode = props.delay_judgement_mode ?? 'whole_line';
  const transferTimeSeconds = props.transfer_time_seconds ?? 180;

  // whole_line モードは「接続線全体の最大遅延」のみで判定する純化されたモード。
  // 区間 (start/end_stop_id) / 乗換駅 (transfer_stop_id) / 方面 (direction_id) は
  // 判定にも放送文にも使用しないため、UI からも非表示にする。
  // transfer_aware モードでは「区間 (開始/終了駅)」は判定にも放送文にも使われないため
  // 同様に UI から非表示にする (乗換駅・方面・乗換時間で判定する純化されたモード)。
  const isWholeLineMode = judgementMode === 'whole_line';
  const isTransferAwareMode = judgementMode === 'transfer_aware';
  const showSectionFields = judgementMode === 'section_scheduled'; // 開始駅 / 終了駅
  const showTransferStopField = !isWholeLineMode; // 乗換駅
  const showDirectionField = !isWholeLineMode; // 方面

  // transfer_aware モードでは「接続線遅延しきい値 (秒)」と単一 SSML テンプレートは使わず、
  // 自列車遅延と同じ表形式 (遅れ分数の幅 × 放送粒度 × 放送パターン) で設定する。
  // これにより遅れ分数の範囲ごとに放送内容を切り替えられる。
  const showThresholdField = !isTransferAwareMode;
  const showSingleSsmlField = !isTransferAwareMode;
  const showDelayPatternsTable = isTransferAwareMode;

  // マスタに無い route_id (古いデータ・別線区への参照など) も選択肢として残し、
  // 既存値が消えてしまわないようにする
  const isUnknownRoute =
    currentRouteId !== '' && !routes.some((r) => r.route_id === currentRouteId);

  // 選択中の接続線 (route_id) に対応する駅一覧 (親が事前取得した routeStops から取り出す)。
  // 念のため stop_sequence 昇順に整列する。
  const stops = sortStopsBySequence(currentRouteId ? routeStops[currentRouteId] : []);

  const startStopId = props.start_stop_id ?? '';

  const endStopId = props.end_stop_id ?? '';

  const transferStopId = props.transfer_stop_id ?? '';

  const directionId = props.direction_id ?? '';

  // 案内文面 (SSML) の {direction_name} プレースホルダに差し込む自由入力テキスト。
  // 判定用 direction_id とは独立した「放送文専用の方面文言」。
  const directionName = props.direction_name ?? '';

  // 方面ドロップダウンの選択肢は、選択中の接続線 (route_id) の方向マスタ (directions) から生成する。
  // direction_id は routes-master.json の各方面に明示された `direction_id` フィールドを
  // 優先採用し、保存値とする。未指定 (古いデータ) の場合は後方互換として配列順 (index) を
  // 文字列化した値 ("0", "1", ...) にフォールバックする。
  //
  // 例) 中総緩は Transformer 側の解釈が他路線と逆 (= 0 が三鷹、1 が千葉) のため、
  // routes-master.json で `direction_id: "0"` を「三鷹方面（A線）」に、
  // `direction_id: "1"` を「千葉方面（B線）」に明示しており、本フィールドを参照することで
  // UI 上で「千葉方面」を選択したときに `direction_id="1"` (= Transformer 側の千葉) が
  // 保存されるようになる。
  const currentRoute = routes.find((r) => r.route_id === currentRouteId);
  const directionOptions = (currentRoute?.directions ?? []).map((d, i) => ({
    value: typeof d.direction_id === 'string' && d.direction_id !== '' ? d.direction_id : String(i),
    label: d.direction_name,
  }));
  // マスタに無い direction_id (古いデータ・別線区への参照など) も選択肢として残し、
  // 既存値が消えてしまわないようにする
  const isUnknownDirection =
    directionId !== '' && !directionOptions.some((o) => o.value === directionId);

  return (
    <div className="contents-fields">
      <label>
        <span className="required">接続線 route_id</span>
        <select
          aria-label="接続線 route_id"
          value={currentRouteId}
          onChange={(e) =>
            onChange({
              ...props,
              route_id: e.target.value,
              delay_threshold_seconds: threshold,
              // 路線を変えたら区間 (開始/終了駅) と乗換駅はリセットする
              // (別路線の stop_id が残らないように)
              start_stop_id: '',
              end_stop_id: '',
              transfer_stop_id: '',
            } as ConnectingLineDelayProperties)
          }
        >
          <option value="">(選択してください)</option>
          {routes.map((r) => (
            <option key={r.route_id} value={r.route_id}>
              {r.route_id}
            </option>
          ))}
          {isUnknownRoute ? (
            <option value={currentRouteId}>{currentRouteId} (マスタ未登録)</option>
          ) : null}
        </select>
      </label>
      {/*
        接続線遅延しきい値 (秒) は whole_line / section_scheduled の単一閾値判定で使用する。
        transfer_aware は遅れ分数の幅ごとに粒度を切り替える delay_patterns で判定するため
        非表示にする (※ 後方互換のため値自体は properties に残す)。
      */}
      {showThresholdField ? (
        <label>
          <span className="required">接続線遅延しきい値 (秒)</span>
          <input
            type="number"
            min={0}
            step={1}
            aria-label="接続線遅延しきい値 (秒)"
            value={threshold}
            onChange={(e) =>
              onChange({
                ...props,
                route_id: props.route_id ?? '',
                delay_threshold_seconds: Number(e.target.value),
              } as ConnectingLineDelayProperties)
            }
          />
        </label>
      ) : null}
      <label>
        <span className="required">判定モード</span>
        <select
          aria-label="判定モード"
          value={judgementMode}
          onChange={(e) => {
            const nextMode = e.target.value as ConnectingDelayJudgementMode;
            // whole_line モードは「接続線全体の最大遅延」のみで判定する純化されたモード。
            // 切り替え時に区間 / 乗換駅 / 方面 の旧入力を properties から落として、
            // DB / JSON にゴミとして残らないようにする。
            const cleared =
              nextMode === 'whole_line'
                ? {
                    start_stop_id: undefined,
                    end_stop_id: undefined,
                    transfer_stop_id: undefined,
                    direction_id: undefined,
                    // 案内文面用の方面文言も whole_line では使用しないので落とす
                    direction_name: undefined,
                  }
                : {};
            // transfer_aware モードに切り替えたとき delay_patterns が未設定なら、
            // 自列車遅延と同等のデフォルトパターン (1分以上〜10分未満 / 5分単位 / ...) を
            // 注入する。既存の delay_patterns があればそのまま尊重する。
            // また transfer_aware では単一 SSML テンプレート / 単一しきい値は使用しないため、
            // それ以外のモードからの切替時にデータを落として DB に残らないようにする。
            // さらに transfer_aware は区間 (開始/終了駅) を判定にも放送文にも使用しないため、
            // section_scheduled から切り替えてきた際に残りがちな start_stop_id / end_stop_id も
            // 落として DB / JSON にゴミとして残らないようにする。
            const transferPatch: Partial<ConnectingLineDelayProperties> =
              nextMode === 'transfer_aware'
                ? {
                    delay_patterns: Array.isArray(props.delay_patterns)
                      ? props.delay_patterns
                      : DEFAULT_CONNECTING_DELAY_PATTERNS.map((p) => ({ ...p })),
                    // transfer_aware は単一 SSML テンプレートを使わないので落とす
                    ssml_template: undefined,
                    // 区間 (開始/終了駅) は transfer_aware では使用しないので落とす
                    start_stop_id: undefined,
                    end_stop_id: undefined,
                  }
                : {};

            // 単一 SSML テンプレート (whole_line / section_scheduled 用) の自動補完。
            // - transfer_aware 以外に切り替えるときは、現在の ssml_template が
            //   「空 / 未設定」または「既知のモード別デフォルト文と完全一致」なら
            //   ユーザ未編集とみなして新モードのデフォルト文に上書きする。
            // - それ以外 (= ユーザがカスタム編集した文言) は尊重する。
            //
            // これにより、新規作成時の初期 SSML 文言 (whole_line のデフォルト) が
            // そのまま残った状態で判定モードを切り替えた場合に、
            // 新モード用のデフォルト文に切り替わるようになる。
            // (Issue: 「whole_line → section_scheduled に切替えても whole_line の文言のまま」)
            const KNOWN_SINGLE_SSML_DEFAULTS: ReadonlySet<string> = new Set<string>([
              CONNECTING_SINGLE_SSML_DEFAULT.whole_line,
              CONNECTING_SINGLE_SSML_DEFAULT.section_scheduled,
            ]);
            const singleSsmlPatch: Partial<ConnectingLineDelayProperties> =
              nextMode !== 'transfer_aware'
                ? (() => {
                    const current =
                      typeof props.ssml_template === 'string' ? props.ssml_template : '';
                    if (current.trim() === '' || KNOWN_SINGLE_SSML_DEFAULTS.has(current)) {
                      return { ssml_template: CONNECTING_SINGLE_SSML_DEFAULT[nextMode] };
                    }
                    return {};
                  })()
                : {};
            onChange({
              ...props,
              ...cleared,
              ...transferPatch,
              ...singleSsmlPatch,
              route_id: props.route_id ?? '',
              delay_threshold_seconds: threshold,
              delay_judgement_mode: nextMode,
            } as ConnectingLineDelayProperties);
          }}
        >
          {CONNECTING_DELAY_MODE_META.map((m) => (
            <option key={m.mode} value={m.mode}>
              {m.label}
            </option>
          ))}
        </select>
      </label>

      <p className="hint" style={{ margin: '4px 0' }}>
        {judgementMode === 'whole_line'
          ? '接続線全体の最大遅延のみで放送可否を判定します。区間や乗換駅・方面の指定は本モードでは使用しません (他モードに任せてください)。'
          : judgementMode === 'section_scheduled'
            ? '指定区間に定刻在線しているはずの便の遅延で判定します。開始駅・終了駅が必須です。'
            : 'お客さまがまさに乗り換えようとしている接続列車 (自列車到着＋乗換時間以降に発車する便) の遅延で判定します。乗換駅・乗換時間が必須です。'}
      </p>
      {/*
        区間 (開始/終了駅) は section_scheduled でのみ使用する。
        whole_line では「接続線全体の最大遅延」のみで判定する純化されたモードのため非表示。
        transfer_aware は区間ではなく乗換駅で判定するため非表示。
      */}
      {showSectionFields ? (
        <>
          <div style={{ display: 'flex', gap: 8 }}>
            <label style={{ flex: 1 }}>
              <span className={judgementMode === 'section_scheduled' ? 'required' : undefined}>
                遅延判定開始駅{judgementMode === 'section_scheduled' ? '' : ' (任意)'}
              </span>
              <StopSelect
                ariaLabel="遅延判定開始駅"
                testId="delay-start-stop-select"
                value={startStopId}
                stops={stops}
                disabled={!currentRouteId}
                onChange={(stopId) =>
                  onChange({
                    ...props,
                    route_id: props.route_id ?? '',
                    delay_threshold_seconds: threshold,
                    start_stop_id: stopId,
                  } as ConnectingLineDelayProperties)
                }
              />
            </label>
            <label style={{ flex: 1 }}>
              <span className={judgementMode === 'section_scheduled' ? 'required' : undefined}>
                遅延判定終了駅{judgementMode === 'section_scheduled' ? '' : ' (任意)'}
              </span>
              <StopSelect
                ariaLabel="遅延判定終了駅"
                testId="delay-end-stop-select"
                value={endStopId}
                stops={stops}
                disabled={!currentRouteId}
                onChange={(stopId) =>
                  onChange({
                    ...props,
                    route_id: props.route_id ?? '',
                    delay_threshold_seconds: threshold,
                    end_stop_id: stopId,
                  } as ConnectingLineDelayProperties)
                }
              />
            </label>
          </div>
          <p className="hint" style={{ margin: '4px 0' }}>
            開始駅・終了駅を指定すると、その区間で遅延があるときのみ放送します。
          </p>
        </>
      ) : null}

      {/*
        乗換駅は section_scheduled / transfer_aware で使用する。
        whole_line では路線名のみを読み上げる純化されたモードのため非表示。

        ユーザ要望により、transfer_aware モードでは「乗換駅 → 乗換時間 (秒)」の
        順で並べる (乗換駅をどこにするか決めてから、その駅での乗換時間を入力するという
        UX の流れに合わせる)。section_scheduled モードでは乗換時間は表示されないため、
        この順序変更は実質的に transfer_aware モードだけに影響する。
      */}
      {showTransferStopField ? (
        <>
          <label>
            <span className={judgementMode === 'transfer_aware' ? 'required' : undefined}>
              乗換駅{judgementMode === 'transfer_aware' ? '' : ' (任意)'}
            </span>
            <StopSelect
              ariaLabel="乗換駅"
              testId="transfer-stop-select"
              value={transferStopId}
              stops={stops}
              disabled={!currentRouteId}
              onChange={(stopId) =>
                onChange({
                  ...props,
                  route_id: props.route_id ?? '',
                  delay_threshold_seconds: threshold,
                  transfer_stop_id: stopId,
                } as ConnectingLineDelayProperties)
              }
            />
          </label>
          <p className="hint" style={{ margin: '4px 0' }}>
            {judgementMode === 'transfer_aware'
              ? '乗り換え先の発車時刻を取得する駅であり、放送文でも読み上げます。'
              : '放送文で読み上げる乗換駅です。未指定の場合は遅延判定開始駅を読み上げます。'}
          </p>
        </>
      ) : null}

      {/* 乗換時間は transfer_aware モードのときのみ表示する */}
      {judgementMode === 'transfer_aware' ? (
        <label>
          <span className="required">乗換時間 (秒)</span>
          <input
            type="number"
            min={0}
            step={1}
            aria-label="乗換時間 (秒)"
            data-testid="transfer-time-seconds-input"
            value={transferTimeSeconds}
            onChange={(e) =>
              onChange({
                ...props,
                route_id: props.route_id ?? '',
                delay_threshold_seconds: threshold,
                transfer_time_seconds: Math.max(0, Math.floor(Number(e.target.value))),
              } as ConnectingLineDelayProperties)
            }
          />
        </label>
      ) : null}

      {/*
        方面は section_scheduled / transfer_aware で使用する。

        whole_line では路線全体の遅延を見るため方面の限定は不要 → 非表示。

        ここの「方面」ドロップダウンは **判定用** (route-stops / stop-times API の
        direction_id クエリパラメータとして使われ、ダイヤ判定対象の方向を絞り込む)。
        放送文 (SSML の {direction_name} プレースホルダ) に差し込む方面文言は
        独立した自由入力テキストボックス (下の「方面 (案内文面用・任意)」) で
        設定する。
      */}
      {showDirectionField ? (
        <label>
          {/*
            画面表示ラベルは「方面 (判定用・任意)」、aria-label は既存テストとの
            互換性のため「方面」のままにする (E2E / AI エージェントの取り違え対策で
            旧来から付与している ariaLabel を変更すると下流のシナリオが壊れるため)。
          */}
          <span>方面 (判定用・任意)</span>

          <select
            aria-label="方面"
            // 画面トップの線区フィルタにも data-testid="direction-select" / aria-label="方面" の
            // <select> が存在するため、E2E や AI エージェントが取り違えないように
            // モーダル内の方面 <select> には別の testid を付与する。
            data-testid="content-direction-select"
            value={directionId}
            disabled={!currentRouteId}
            onChange={(e) =>
              onChange({
                ...props,
                route_id: props.route_id ?? '',
                delay_threshold_seconds: threshold,
                direction_id: e.target.value,
              } as ConnectingLineDelayProperties)
            }
          >
            <option value="">(指定なし)</option>
            {directionOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            {isUnknownDirection ? (
              <option value={directionId}>{directionId} (マスタ未登録)</option>
            ) : null}
          </select>
        </label>
      ) : null}

      {/*
        案内文面用の方面文言 (自由入力テキスト)。
        判定用 direction_id とは独立した、放送文 (SSML の {direction_name}
        プレースホルダ) に差し込まれる任意テキスト。
        - section_scheduled / transfer_aware モードのときに使用する。
        - whole_line モードでは判定にも放送文にも使われない (バックエンドで無視) ため非表示。
      */}
      {showDirectionField ? (
        <>
          <label>
            <span>方面 (案内文面用・任意)</span>
            {/*
              テキストボックスには **駅名のみ** を入力する想定。
              放送文 (SSML) では Lambda 側で末尾に固定文言「方面行き」を結合してから
              {direction_name} プレースホルダへ展開する (例: 入力「千葉」→ 放送文「千葉方面行き」)。
              結合が行われることを視覚的に伝えるため、テキストボックスの右隣に
              固定ラベル <span>方面行き</span> を併記する。
            */}
            <div className="direction-name-input-row">
              <input
                type="text"
                aria-label="方面 (案内文面用)"
                data-testid="content-direction-name-input"
                value={directionName}
                placeholder="例: 千葉"
                onChange={(e) =>
                  onChange({
                    ...props,
                    route_id: props.route_id ?? '',
                    delay_threshold_seconds: threshold,
                    // 空文字は undefined にして DB / JSON にゴミを残さない
                    direction_name: e.target.value === '' ? undefined : e.target.value,
                  } as ConnectingLineDelayProperties)
                }
              />
              <span
                className="direction-name-suffix"
                data-testid="content-direction-name-suffix"
                aria-hidden="true"
              >
                方面行き
              </span>
            </div>
          </label>
          <p
            className="hint"
            style={{ margin: '4px 0' }}
            data-testid="connecting-delay-direction-name-hint"
          >
            放送文では入力値の末尾に <code>方面行き</code> が自動で付与されます (例:
            「千葉」→「千葉方面行き」)。 空欄なら方面は読み上げられません。判定用「方面
            (判定用・任意)」とは独立して指定できます。
          </p>
        </>
      ) : null}

      {/*
        transfer_aware モードでは「遅れ分数の幅 × 放送粒度 × 放送パターン」を表形式で
        編集できるようにする (自列車遅延と同じ DelayPatternRow を再利用)。
        他モードでは従来通り単一の SSML テンプレート (delay_threshold_seconds と組合せ) を使う。
      */}
      {showDelayPatternsTable ? (
        <ConnectingLineDelayPatternsTable props={props} threshold={threshold} onChange={onChange} />
      ) : null}

      {/*
        単一 SSML テンプレートは whole_line / section_scheduled でのみ使用する。
        transfer_aware は delay_patterns 側の SSML テンプレートを使うため非表示にし、
        単一テンプレート (ssml_template) は properties に残さない設計とする。
      */}
      {showSingleSsmlField ? (
        <>
          <label>
            {/*
             * 画面表示ラベルと aria-label は完全一致させること。
             * 1 つの放送地点に「自列車遅延」と「接続線遅延」の両方を設定するケースがあり、
             * 自列車遅延側にも SSML テンプレート系の <textarea> が存在するため、
             * E2E / AI エージェントが取り違えないように「(接続線遅延)」を必ず付与する。
             * 併せて data-testid="content-ssml-template" でも一意に取得できるようにする。
             */}
            <span>SSML テンプレート (接続線遅延)</span>
            <textarea
              rows={2}
              aria-label="SSML テンプレート (接続線遅延)"
              data-testid="content-ssml-template"
              // ユーザ要望: SSML 入力欄は必須項目。
              // 新規作成時 / モード切替時にデフォルト文言が初期値として入っているため、
              // ユーザがクリアしてゼロベース入力に入った状態のみ空文字になる。
              required
              value={props.ssml_template ?? ''}
              onChange={(e) =>
                onChange({
                  ...props,
                  route_id: props.route_id ?? '',
                  delay_threshold_seconds: threshold,
                  ssml_template: e.target.value,
                } as ConnectingLineDelayProperties)
              }
              /*
                判定モード別の placeholder。
                - whole_line: Lambda 側で from_station が常に空文字に強制されるため、
                  `{from_prefix}` / `{from_station}` / `{direction_name}` も空文字に展開される。
                  ユーザー混乱を避けるために placeholder からはこれらを除いた文面を表示する。
                - section_scheduled: `{from_prefix}` が「<駅名>駅からお」に展開されるため
                  Lambda の DEFAULT_CONNECTING_DELAY_TEMPLATE と同じ文面を placeholder にする。

                ※ 既存テスト (BroadcastContentsEditor.test.tsx) が placeholder の文面を
                  正確にアサートしているため、placeholder 自体は維持する。実値の方は
                  defaultProperties / モード切替ロジックで自動補完する。
              */
              placeholder={
                isWholeLineMode
                  ? '<speak>乗換の{route_name}は、現在{delay_seconds}秒ほど遅れています</speak>'
                  : '<speak>{from_prefix}乗換の{route_name}{direction_name}は、現在{delay_seconds}秒ほど遅れています</speak>'
              }
            />
          </label>
          <p className="hint" style={{ margin: '4px 0' }}>
            SSML
            は必須項目です。デフォルト文言があらかじめ入力されているので、必要に応じて書き換えてください。
          </p>
          {/*
            接続線遅延テンプレートで使えるプレースホルダの凡例。
            判定モード別に出し分ける:
            - whole_line: Lambda 側で乗換駅・方面が放送されないため、
              `{from_prefix}` / `{from_station}` / `{direction_name}` は凡例から除外し、
              注釈 (note) として「空欄として扱われます」を明示する。
            - section_scheduled: 全プレースホルダを列挙する。
          */}
          {isWholeLineMode ? (
            <PlaceholderLegend
              testId="connecting-delay-route-hint"
              items={CONNECTING_WHOLE_LINE_PLACEHOLDERS}
              note={
                <span>
                  ※ 接続線全体の遅延モードでは乗換駅・方面は読み上げられないため、{' '}
                  <code>{'{from_station}'}</code> / <code>{'{from_prefix}'}</code> /{' '}
                  <code>{'{direction_name}'}</code> はテンプレートに書いても空欄として扱われます。
                </span>
              }
            />
          ) : (
            <PlaceholderLegend
              testId="connecting-delay-route-hint"
              items={CONNECTING_SECTION_SCHEDULED_PLACEHOLDERS}
            />
          )}
        </>
      ) : null}
    </div>
  );
}

/**
 * 接続線遅延 (transfer_aware モード) 用の表形式エディタ。
 *
 * 自列車遅延の `SelfTrainDelayFields` と同じ構造で、遅れ分数の幅ごとに 1 行ずつ
 * 「遅れ分数 / 放送粒度 / 放送パターン (SSMLテンプレート) / 行操作 (＋−)」を表示する。
 * 行コンポーネント (`DelayPatternRow`) は自列車遅延と共通化しているため、testid プレフィックスを
 * `connecting-line-delay-ssml-template` に差し替えて自列車遅延の <textarea> と区別する。
 */
function ConnectingLineDelayPatternsTable({
  props,
  threshold,
  onChange,
}: {
  props: Partial<ConnectingLineDelayProperties>;
  threshold: number;
  onChange: (props: ConnectingLineDelayProperties) => void;
}): React.JSX.Element {
  const existingPatterns: DelayBroadcastPattern[] = Array.isArray(props.delay_patterns)
    ? props.delay_patterns
    : [];
  const patterns: DelayBroadcastPattern[] =
    existingPatterns.length > 0
      ? existingPatterns
      : [createDefaultPattern(CONNECTING_GRANULARITY_DEFAULT_SSML)];

  /** delay_patterns を更新し、必須プロパティ (route_id / threshold) を維持して onChange を呼ぶ */
  const emit = (nextPatterns: DelayBroadcastPattern[]): void => {
    onChange({
      ...props,
      route_id: props.route_id ?? '',
      delay_threshold_seconds: threshold,
      delay_patterns: nextPatterns,
    } as ConnectingLineDelayProperties);
  };

  const updatePattern = (index: number, patch: Partial<DelayBroadcastPattern>): void => {
    const next = patterns.slice();
    const current = next[index]!;
    // 粒度が変わったら接続線 transfer_aware 用のデフォルト SSML 文に上書きする。
    // (ユーザ要望: 「粒度を変えたら元々のデフォルト値を再設定して」)
    if (patch.granularity && patch.granularity !== current.granularity) {
      next[index] = {
        ...current,
        ...patch,
        ssml_template: CONNECTING_GRANULARITY_DEFAULT_SSML[patch.granularity],
      };
    } else {
      next[index] = { ...current, ...patch };
    }
    emit(next);
  };

  const addPatternAfter = (index: number): void => {
    const next = patterns.slice();
    next.splice(index + 1, 0, createDefaultPattern(CONNECTING_GRANULARITY_DEFAULT_SSML));
    emit(next);
  };

  const removePattern = (index: number): void => {
    if (patterns.length <= 1) return;
    const next = patterns.slice();
    next.splice(index, 1);
    emit(next);
  };

  return (
    <div className="connecting-line-delay-patterns" data-testid="connecting-line-delay-patterns">
      <table className="delay-patterns-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '4px 6px', whiteSpace: 'nowrap' }}>
              遅れ分数
            </th>
            <th style={{ textAlign: 'left', padding: '4px 6px', whiteSpace: 'nowrap' }}>
              放送粒度
            </th>
            <th style={{ textAlign: 'left', padding: '4px 6px' }}>
              放送パターン (SSMLテンプレート)
            </th>
            <th
              aria-label="行操作"
              style={{ textAlign: 'center', padding: '4px 6px', whiteSpace: 'nowrap' }}
            />
          </tr>
        </thead>
        <tbody>
          {patterns.map((pattern, i) => (
            <DelayPatternRow
              key={i}
              index={i}
              pattern={pattern}
              canRemove={patterns.length > 1}
              ssmlTestIdPrefix="connecting-line-delay-ssml-template"
              placeholderTemplates={CONNECTING_GRANULARITY_DEFAULT_SSML}
              onUpdate={(patch) => updatePattern(i, patch)}
              onAdd={() => addPatternAfter(i)}
              onRemove={() => removePattern(i)}
            />
          ))}
        </tbody>
      </table>

      <p className="hint" style={{ margin: '8px 0 4px' }}>
        各行の「遅れ分数」の範囲に隙間が生じないよう注意して設定してください。
        各行の「＋」で行を追加、「−」で行を削除できます。 SSML
        欄は必須項目です。各放送粒度のデフォルト文言があらかじめ入力されているので、必要に応じて書き換えてください。
      </p>
      <PlaceholderLegend
        testId="connecting-line-delay-pattern-hint"
        items={CONNECTING_TRANSFER_AWARE_PLACEHOLDERS}
      />
    </div>
  );
}

/**
 * 接続線遅延 (transfer_aware モード) の「放送パターン (SSMLテンプレート)」入力欄で
 * 使えるプレースホルダ一覧。乗換駅名 (`{from_station}`) と方面 (`{direction_name}`) が
 * 自列車遅延と違って利用できる点が特徴。
 */
const CONNECTING_TRANSFER_AWARE_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{delay_minutes_1}', description: '1分単位で切り捨てた遅延分数' },
  { token: '{delay_minutes_5}', description: '5分単位で切り捨てた遅延分数' },
  { token: '{delay_minutes_10}', description: '10分単位で切り捨てた遅延分数' },
  { token: '{from_station}', description: '乗換駅名' },
  { token: '{from_prefix}', description: '「<駅名>駅から」(乗換駅が無ければ空欄)' },
  { token: '{direction_name}', description: '方面 (末尾に「方面行き」が自動付与)' },
  { token: '{route_name}', description: '接続線の一般的な線名 (未登録時は {route_id} の値)' },
  { token: '{route_id}', description: 'ATOS 線名コード (路線ID)' },
];

/**
 * 接続線遅延 (whole_line モード) の単一 SSML テンプレートで使えるプレースホルダ一覧。
 *
 * whole_line モードでは Lambda 側で乗換駅・方面が放送されないため、
 * `{from_prefix}` / `{from_station}` / `{direction_name}` は凡例から除外し、
 * 注釈 (note) として「空欄として扱われます」を凡例の下に明示する。
 */
const CONNECTING_WHOLE_LINE_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{route_name}', description: '一般的な線名 (例: 京浜東北線)' },
  { token: '{route_id}', description: 'ATOS 線名コード (例: 京浜)' },
  { token: '{route}', description: '{route_name} と同じ意味' },
  { token: '{delay_seconds}', description: '遅延秒数' },
  { token: '{delay_minutes}', description: '切り上げ・最低1分の遅延分数' },
  { token: '{delay}', description: '{delay_seconds} と同じ意味' },
];

/**
 * 接続線遅延 (section_scheduled モード) の単一 SSML テンプレートで使えるプレースホルダ一覧。
 * 乗換駅 (`{from_station}` / `{from_prefix}`) と方面 (`{direction_name}`) も
 * 利用可能 (Lambda 側で展開される)。
 */
const CONNECTING_SECTION_SCHEDULED_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{route_name}', description: '一般的な線名 (例: 京浜東北線)' },
  { token: '{route_id}', description: 'ATOS 線名コード (例: 京浜)' },
  { token: '{route}', description: '{route_name} と同じ意味' },
  { token: '{from_station}', description: '乗換駅名' },
  { token: '{from_prefix}', description: '「<駅名>駅からお」(乗換駅が無ければ空欄)' },
  { token: '{direction_name}', description: '方面 (末尾に「方面行き」が自動付与)' },
  { token: '{delay_seconds}', description: '遅延秒数' },
  { token: '{delay_minutes}', description: '切り上げ・最低1分の遅延分数' },
  { token: '{delay}', description: '{delay_seconds} と同じ意味' },
];

// ---------------------------------------------------------------------------
// 接続線遅延 (乗換駅ベース / part2) 用のプレースホルダ凡例定数。
// ---------------------------------------------------------------------------

/**
 * 冒頭 SSML テンプレート (`ssml_intro_template`) で使えるプレースホルダ一覧。
 * 乗換駅名のみを扱うシンプルな凡例。
 */
const CONNECTING_STATION_INTRO_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{transfer_station}', description: '乗換駅名 (例: 赤羽)' },
];

/**
 * 接続線ブロック SSML テンプレート (`ssml_line_template`) で使えるプレースホルダ一覧。
 * 方面部分文を読点連結した文字列 `{directions_text}` を必ず含めることを想定する。
 */
const CONNECTING_STATION_LINE_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{route_name}', description: '接続線の一般的な線名 (例: 埼京線)' },
  { token: '{route_id}', description: 'ATOS 線名コード (例: 埼京)' },
  { token: '{route}', description: '{route_name} と同じ意味' },
  {
    token: '{directions_text}',
    description: '方面部分文を読点で連結した文字列 (方面ごとの遅延案内)',
  },
  { token: '{transfer_station}', description: '乗換駅名 (例: 赤羽)' },
];

/**
 * 方面部分文 SSML テンプレート (`lines[j].delay_patterns[k].ssml_template`) で
 * 使えるプレースホルダ一覧。part1 の transfer_aware 用凡例に近いが、乗換駅
 * (`{from_station}` / `{from_prefix}`) は使わず、代わりに `{transfer_station}` を使う。
 */
const CONNECTING_STATION_DIRECTION_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{direction_name}', description: '方面名 (例: 新宿)。必須項目' },
  { token: '{delay_minutes_1}', description: '1分単位で切り捨てた遅延分数' },
  { token: '{delay_minutes_5}', description: '5分単位で切り捨てた遅延分数' },
  { token: '{delay_minutes_10}', description: '10分単位で切り捨てた遅延分数' },
  { token: '{delay_minutes}', description: '切り上げ・最低1分の遅延分数' },
  { token: '{delay_seconds}', description: '遅延秒数' },
  { token: '{delay}', description: '{delay_seconds} と同じ意味' },
  { token: '{route_name}', description: '接続線の一般的な線名' },
  { token: '{route_id}', description: 'ATOS 線名コード' },
  { token: '{transfer_station}', description: '乗換駅名' },
];

/**
 * 駅選択ドロップダウン。
 * - stops は stop_sequence 昇順で渡される前提。
 * - value が stops に存在しない (= マスタ未取得 / 別バージョン) 場合も選択肢として残し、
 *   既存値が消えないようにする。
 */
function StopSelect({
  ariaLabel,
  testId,
  value,
  stops,
  disabled,
  onChange,
}: {
  ariaLabel: string;
  /**
   * data-testid 属性。E2E 等から `[data-testid="..."]` で一意に拾うため。
   * aria-label が「乗換駅」「遅延判定開始駅」のように似通っており AI/E2E が
   * 取り違えやすいので、明示的な testid を渡せるようにしている。
   */
  testId?: string;
  value: string;
  stops: RouteStop[];
  disabled?: boolean;
  onChange: (stopId: string) => void;
}): React.JSX.Element {
  const isUnknownStop = value !== '' && !stops.some((s) => s.stop_id === value);
  return (
    <select
      aria-label={ariaLabel}
      data-testid={testId}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">(指定なし)</option>
      {stops.map((s) => (
        <option key={s.stop_id} value={s.stop_id}>
          {s.stop_name || s.stop_id}
        </option>
      ))}
      {isUnknownStop ? <option value={value}>{value} (マスタ未取得)</option> : null}
    </select>
  );
}

/**
 * 既存の properties から入力モードを推測する。
 * - `input_mode` が明示されていればそれを使う。
 * - `audio_key` があれば audio、`ssml` があれば ssml、それ以外は text。
 */
function resolveInputMode(props: Partial<FreeTextProperties>): FreeTextInputMode {
  if (props.input_mode) return props.input_mode;
  if (props.audio_key && props.audio_key.trim() !== '') return 'audio';
  if (props.ssml && props.ssml.trim() !== '') return 'ssml';
  return 'text';
}

const FREE_TEXT_MODE_META: ReadonlyArray<{ mode: FreeTextInputMode; label: string }> = [
  { mode: 'text', label: '通常テキスト' },
  { mode: 'ssml', label: 'SSML' },
  { mode: 'audio', label: '音声ファイル' },
];

/**
 * 入力方法ラジオボタンの data-testid。
 *
 * E2E / AI エージェントが「通常テキスト」「SSML」「音声ファイル」のラジオを
 * 安定して選択できるようにするため、各ラジオに明示的な data-testid を付与する。
 *
 * ※ 画面ラベルは「通常テキスト」だが、value 属性は FreeTextInputMode のキー
 *   ("text" / "ssml" / "audio") であり、"plain" のような別名ではない点に注意。
 *   AI エージェントが画面ラベルから value を推測して `value="plain"` 等を
 *   投入する事故を避ける目的でも、testid 経由でのクリックを推奨する。
 */
const FREE_TEXT_MODE_TESTID: Record<FreeTextInputMode, string> = {
  text: 'content-free-text-mode-text',
  ssml: 'content-free-text-mode-ssml',
  audio: 'content-free-text-mode-audio',
};

function FreeTextFields({
  props,
  onChange,
  onUploadAudio,
  onPreviewSsml,
}: {
  props: Partial<FreeTextProperties>;
  onChange: (props: FreeTextProperties) => void;
  onUploadAudio?: (file: File) => Promise<string>;
  onPreviewSsml?: PreviewSsmlHandler;
}): React.JSX.Element {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const mode = resolveInputMode(props);
  const plainText = props.text ?? '';
  const ssmlText = props.ssml ?? '';

  // プレビュー対象 (audio モードはプレビュー不可)
  const previewableText = mode === 'ssml' ? ssmlText : mode === 'text' ? plainText : '';
  const canPreview = mode !== 'audio' && previewableText.trim() !== '';

  const handleModeChange = (next: FreeTextInputMode): void => {
    onChange({ ...props, input_mode: next });
  };

  const handlePreview = async (): Promise<void> => {
    if (!onPreviewSsml || !canPreview) return;
    const input = mode === 'ssml' ? { ssml: ssmlText } : { text: plainText, isSsml: false };
    setPreviewError(null);
    setPreviewing(true);
    try {
      // 直前のプレビュー URL は解放しておく
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const url = await onPreviewSsml(input);
      setPreviewUrl(url);
    } catch (err) {
      setPreviewError((err as Error).message || '音声プレビューの生成に失敗しました');
    } finally {
      setPreviewing(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    // input を毎回リセットして、同じファイルを連続選択しても onChange が発火するようにする
    e.target.value = '';
    if (!file || !onUploadAudio) return;
    setUploadError(null);
    setUploading(true);
    try {
      const audioKey = await onUploadAudio(file);
      onChange({ ...props, input_mode: 'audio', audio_key: audioKey });
    } catch (err) {
      setUploadError((err as Error).message || '音声ファイルのアップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAudio = (): void => {
    const next = { ...props };
    delete next.audio_key;
    onChange(next);
  };

  return (
    <div className="contents-fields">
      <fieldset className="free-text-mode-switch" style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={{ padding: 0 }}>入力方法</legend>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {FREE_TEXT_MODE_META.map((m) => (
            <label key={m.mode} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <input
                type="radio"
                /*
                 * E2E / AI エージェントが「通常テキスト」「SSML」「音声ファイル」の
                 * ラジオボタンを安定して特定できるよう data-testid を付与する。
                 *
                 * 画面ラベルは日本語 ("通常テキスト" など) だが value は
                 * FreeTextInputMode キー ("text" / "ssml" / "audio") であり、
                 * "plain" のような別名ではない。AI が画面ラベルから推測した
                 * セレクタ (例: input[value='plain']) を投入して 3 連続失敗で
                 * ステップが中止される事故が報告されたため、testid を付ける。
                 */
                data-testid={FREE_TEXT_MODE_TESTID[m.mode]}
                aria-label={`入力方法: ${m.label}`}
                value={m.mode}
                checked={mode === m.mode}
                onChange={() => handleModeChange(m.mode)}
              />
              {m.label}
            </label>
          ))}
        </div>
      </fieldset>

      {mode === 'text' ? (
        <label>
          {/*
           * 画面表示ラベル (<span>) と aria-label は完全一致させること。
           * 不一致だと E2E / AI エージェントが画面ラベルから要素を特定できず、
           * モーダル内の他のテキスト入力欄 (説明欄: data-testid="description-input") に
           * 誤入力してしまう事象が発生する。
           * 併せて data-testid="content-free-text-plain" でも一意に取得できるようにする。
           */}
          <span>放送文面 (通常テキスト)</span>
          <textarea
            rows={4}
            aria-label="放送文面 (通常テキスト)"
            data-testid="content-free-text-plain"
            value={plainText}
            onChange={(e) =>
              onChange({
                ...props,
                input_mode: 'text',
                text: e.target.value,
              })
            }
            placeholder="まもなく終点です"
          />
        </label>
      ) : null}

      {mode === 'ssml' ? (
        <label>
          {/*
           * 画面表示ラベル (<span>) と aria-label は完全一致 ("放送文面 SSML")。
           * 併せて data-testid="content-free-text-ssml" でも一意に取得できるようにする。
           */}
          <span>放送文面 SSML</span>
          <textarea
            rows={4}
            aria-label="放送文面 SSML"
            data-testid="content-free-text-ssml"
            value={ssmlText}
            onChange={(e) =>
              onChange({
                ...props,
                input_mode: 'ssml',
                ssml: e.target.value,
              })
            }
            placeholder="<speak>まもなく終点です</speak>"
          />
        </label>
      ) : null}

      {mode !== 'audio' && onPreviewSsml ? (
        <div className="ssml-preview-field">
          <button
            type="button"
            onClick={() => void handlePreview()}
            disabled={previewing || !canPreview}
            aria-label="音声プレビュー"
          >
            {previewing ? '生成中...' : '▶ 音声プレビュー'}
          </button>
          {previewUrl ? (
            <audio
              controls
              src={previewUrl}
              data-testid="ssml-preview-audio"
              style={{ display: 'block', marginTop: '4px' }}
            >
              <track kind="captions" />
            </audio>
          ) : null}
          {previewError ? (
            <div className="error-message" role="alert">
              {previewError}
            </div>
          ) : null}
        </div>
      ) : null}

      {mode === 'audio' ? (
        <div className="audio-upload-field">
          <span>放送音声ファイル (MP3)</span>
          {props.audio_key ? (
            <div className="audio-upload-status" data-testid="audio-uploaded">
              <span>アップロード済み: {props.audio_key}</span>
              <button type="button" className="danger" onClick={handleRemoveAudio}>
                音声を削除
              </button>
            </div>
          ) : (
            <p className="hint" style={{ margin: '4px 0' }}>
              MP3
              音声ファイルをアップロードしてください。アップロードした音声がそのまま放送されます。
            </p>
          )}
          <input
            type="file"
            accept="audio/mpeg,.mp3"
            aria-label="放送音声ファイル (MP3)"
            disabled={uploading || !onUploadAudio}
            onChange={(e) => void handleFileChange(e)}
          />
          {uploading ? <span className="hint">アップロード中...</span> : null}
          {uploadError ? (
            <div className="error-message" role="alert">
              {uploadError}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ===========================================================================
// 接続線遅延 (乗換駅ベース / part2 / `connecting_line_delay_by_station`) の UI
// ===========================================================================
//
// 三層構造:
//   ConnectingLineDelayByStationFields      … コンテンツ全体 (乗換駅+接続線一覧+冒頭SSML)
//     └── ConnectingLineBlock (接続線1本)   … route_id / 判定モード / 乗換時間 / 方面一覧 / 遅延放送パターン
//            └── ConnectingDirectionBlock   … direction_id / direction_name / 区間 (section時)
//
// 状態管理は上流 (BroadcastContentsEditor) がコントロールドに握るため、各コンポーネントは
// 「現在の値」と「更新関数」を props で受け取り、内部状態は保持しない。

/**
 * 接続線遅延 (乗換駅ベース / part2) のトップレベルフィールド。
 * 乗換駅、冒頭SSML、接続線ブロック一覧を編集する。
 */
function ConnectingLineDelayByStationFields({
  props,
  routes,
  routeStops,
  parentRouteId,
  onChange,
}: {
  props: Partial<ConnectingLineDelayByStationProperties>;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  /**
   * 親フォーム側で編集中の放送地点の「自路線」の route_id。
   * 乗換駅ドロップダウンの選択肢を、この route_id に対応する駅一覧のみに絞り込むために使う。
   * 詳細は `BroadcastContentsEditorProps.parentRouteId` を参照。
   */
  parentRouteId?: string;
  onChange: (props: ConnectingLineDelayByStationProperties) => void;
}): React.JSX.Element {
  const transferStopId = props.transfer_stop_id ?? '';
  const introTemplate = props.ssml_intro_template ?? CONNECTING_STATION_INTRO_DEFAULT_SSML;
  const lines: ConnectingLineEntry[] = Array.isArray(props.lines) ? props.lines : [];

  // 「乗換駅」ドロップダウンの選択肢は、親フォームで選択中の路線 (parentRouteId) の駅一覧を使う。
  //
  // 親から渡される `routeStops` は複数 route_id ぶんが 1 つの Record にまとまっている
  // (BroadcastPointsPage が全路線ぶん事前取得しているため) ので、明示的に
  // `routeStops[parentRouteId]` で自路線に絞り込む必要がある。
  //
  // 以前は `Object.values(routeStops)[0]` で「Record の先頭キー」を暗黙採用していたが、
  // Record の並び順は保証されず、複数 route_id が入っていると自路線とは無関係な路線
  // (例: 中総緩の地点を編集中なのに京浜東北線の駅が候補に出る) の駅一覧が
  // 表示される不具合が発生していたため、`parentRouteId` による明示指定に変更した。
  //
  // `parentRouteId` が未指定 / 該当する駅一覧が routeStops に無い場合は空配列とし、
  // 誤って別路線の駅が表示されないようにする (乗換駅ドロップダウンは
  // 「(指定なし)」のみとなる)。
  const parentStops = sortStopsBySequence(parentRouteId ? (routeStops[parentRouteId] ?? []) : []);

  /** properties 全体を新しい値でまとめて置き換えるヘルパ */
  const emit = (patch: Partial<ConnectingLineDelayByStationProperties>): void => {
    onChange({
      transfer_stop_id: transferStopId,
      ssml_intro_template: introTemplate,
      lines,
      ...props,
      ...patch,
    } as ConnectingLineDelayByStationProperties);
  };

  /** 接続線一覧の index 番目を patch で更新する */
  const updateLine = (index: number, next: ConnectingLineEntry): void => {
    const nextLines = lines.slice();
    nextLines[index] = next;
    emit({ lines: nextLines });
  };

  /** 接続線を index の直後に追加する */
  const addLineAfter = (index: number): void => {
    const nextLines = lines.slice();
    nextLines.splice(index + 1, 0, createDefaultConnectingStationLine());
    emit({ lines: nextLines });
  };

  /** 接続線を先頭に追加する (lines が空のときの初回追加用) */
  const addLineFirst = (): void => {
    emit({ lines: [...lines, createDefaultConnectingStationLine()] });
  };

  /** 接続線を index から削除する (最後の 1 件は残す) */
  const removeLine = (index: number): void => {
    if (lines.length <= 1) return;
    const nextLines = lines.slice();
    nextLines.splice(index, 1);
    emit({ lines: nextLines });
  };

  /** 接続線を上へ移動 */
  const moveLineUp = (index: number): void => {
    if (index <= 0) return;
    const nextLines = lines.slice();
    const [moved] = nextLines.splice(index, 1);
    if (!moved) return;
    nextLines.splice(index - 1, 0, moved);
    emit({ lines: nextLines });
  };
  /** 接続線を下へ移動 */
  const moveLineDown = (index: number): void => {
    if (index >= lines.length - 1) return;
    const nextLines = lines.slice();
    const [moved] = nextLines.splice(index, 1);
    if (!moved) return;
    nextLines.splice(index + 1, 0, moved);
    emit({ lines: nextLines });
  };

  return (
    <div className="contents-fields">
      {/* 乗換駅 (必須): 親路線に属する駅から選ぶ */}
      <label>
        <span className="required">乗換駅</span>
        <StopSelect
          ariaLabel="乗換駅"
          testId="connecting-station-transfer-stop-select"
          value={transferStopId}
          stops={parentStops}
          onChange={(stopId) => emit({ transfer_stop_id: stopId })}
        />
      </label>
      <p className="hint" style={{ margin: '4px 0' }}>
        自路線で選択した路線の駅から、乗換駅を 1 つ選んでください (例:
        「赤羽」)。この駅を主語として、下の接続線ブロックが順に読み上げられます。
      </p>

      {/* 冒頭 SSML テンプレート (必須) */}
      <label>
        <span className="required">冒頭 SSML テンプレート</span>
        <input
          type="text"
          aria-label="冒頭 SSML テンプレート"
          data-testid="connecting-station-intro-template"
          required
          value={introTemplate}
          placeholder={CONNECTING_STATION_INTRO_DEFAULT_SSML}
          onChange={(e) => emit({ ssml_intro_template: e.target.value })}
        />
      </label>
      <PlaceholderLegend
        testId="connecting-station-intro-hint"
        items={CONNECTING_STATION_INTRO_PLACEHOLDERS}
      />

      {/* 接続線ブロック一覧 */}
      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <strong>接続線 ({lines.length} 件)</strong>
          <button
            type="button"
            data-testid="connecting-station-add-line-button"
            aria-label="接続線を追加"
            onClick={addLineFirst}
          >
            ＋ 接続線を追加
          </button>
        </div>
        {lines.length === 0 ? (
          <div className="empty-state" style={{ padding: '8px' }}>
            接続線が設定されていません。「＋ 接続線を追加」から作成してください。
          </div>
        ) : null}
        <ol className="connecting-station-line-list" style={{ listStyle: 'none', padding: 0 }}>
          {lines.map((line, i) => (
            <li
              key={i}
              className="connecting-station-line-item"
              data-testid={`connecting-station-line-${i}`}
              style={{
                border: '1px solid #ddd',
                borderRadius: 4,
                padding: '8px 12px',
                marginTop: 8,
              }}
            >
              <ConnectingLineBlock
                index={i}
                line={line}
                routes={routes}
                routeStops={routeStops}
                canRemove={lines.length > 1}
                isFirst={i === 0}
                isLast={i === lines.length - 1}
                onChange={(next) => updateLine(i, next)}
                onAdd={() => addLineAfter(i)}
                onRemove={() => removeLine(i)}
                onMoveUp={() => moveLineUp(i)}
                onMoveDown={() => moveLineDown(i)}
              />
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

/**
 * 接続線遅延 (乗換駅ベース / part2) の 1 接続線ぶんのブロック。
 * ConnectingLineDelayByStationFields.lines[i] を編集する。
 *
 * 内部で「route_id 選択 → 判定モード → 乗換時間 (transfer_aware) → 方面 (1〜2件) →
 * 接続線ブロック SSML → 遅延放送パターン (方面部分文SSML)」を並べる。
 * 判定モードによる項目表示切替のルールは part1 (`ConnectingLineDelayFields`) と同じ。
 */
function ConnectingLineBlock({
  index,
  line,
  routes,
  routeStops,
  canRemove,
  isFirst,
  isLast,
  onChange,
  onAdd,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  index: number;
  line: ConnectingLineEntry;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  canRemove: boolean;
  isFirst: boolean;
  isLast: boolean;
  onChange: (next: ConnectingLineEntry) => void;
  onAdd: () => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}): React.JSX.Element {
  const routeId = line.route_id ?? '';
  const judgementMode: ConnectingDelayJudgementMode = line.delay_judgement_mode ?? 'whole_line';
  const transferTimeSeconds = line.transfer_time_seconds ?? 180;
  const lineTemplate = line.ssml_line_template ?? CONNECTING_STATION_LINE_DEFAULT_SSML;
  const directions: ConnectingDirectionEntry[] = Array.isArray(line.directions)
    ? line.directions
    : [];
  const patterns: DelayBroadcastPattern[] = Array.isArray(line.delay_patterns)
    ? line.delay_patterns
    : DEFAULT_CONNECTING_STATION_DELAY_PATTERNS.map((p) => ({ ...p }));

  // 判定モードによる項目表示切替 (part1 と同じ考え方)。
  //   whole_line       : 方面ドロップダウン (direction_id) / 区間 (start/end_stop_id) は非表示。
  //                      direction_name (放送文用) だけは表示 (方面別に案内する用途は残るため)。
  //   section_scheduled: 方面ごとに区間 (start/end_stop_id) 必須。
  //   transfer_aware   : 乗換時間 (秒) 必須。区間は不要。
  const isTransferAwareMode = judgementMode === 'transfer_aware';

  // route_id マスタに未登録の値も残す (part1 と同じ)。
  const isUnknownRoute = routeId !== '' && !routes.some((r) => r.route_id === routeId);

  // 表示ラベル用: 接続線の一般的な線名 (routes-master.json の route_name)。
  const currentRoute = routes.find((r) => r.route_id === routeId);
  const routeDisplayName = currentRoute?.route_name || routeId;

  /** ブロック全体を patch で更新 */
  const emit = (patch: Partial<ConnectingLineEntry>): void => {
    onChange({ ...line, ...patch });
  };

  return (
    <div className="connecting-line-block" data-testid={`connecting-line-block-${index}`}>
      {/* ブロックヘッダ: ラベル + 操作ボタン (上/下/削除/追加) */}
      <div
        className="connecting-line-block-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 6,
          flexWrap: 'wrap',
        }}
      >
        <strong>
          接続線 #{index + 1}
          {routeId ? `（${routeDisplayName}）` : ''}
        </strong>
        <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
          <button
            type="button"
            aria-label={`接続線#${index + 1} を上へ`}
            data-testid={`connecting-line-move-up-${index}`}
            disabled={isFirst}
            onClick={onMoveUp}
          >
            ↑
          </button>
          <button
            type="button"
            aria-label={`接続線#${index + 1} を下へ`}
            data-testid={`connecting-line-move-down-${index}`}
            disabled={isLast}
            onClick={onMoveDown}
          >
            ↓
          </button>
          <button
            type="button"
            aria-label={`接続線#${index + 1} の後に追加`}
            data-testid={`connecting-line-add-${index}`}
            onClick={onAdd}
          >
            ＋ 追加
          </button>
          {canRemove ? (
            <button
              type="button"
              className="danger"
              aria-label={`接続線#${index + 1} を削除`}
              data-testid={`connecting-line-remove-${index}`}
              onClick={onRemove}
            >
              × 削除
            </button>
          ) : null}
        </div>
      </div>

      {/* 接続線 route_id */}
      <label>
        <span className="required">接続線 route_id</span>
        <select
          aria-label={`接続線#${index + 1} route_id`}
          data-testid={`connecting-line-route-id-${index}`}
          value={routeId}
          onChange={(e) =>
            emit({
              route_id: e.target.value,
              // 路線を変えたら方面の区間駅はリセット (別路線の stop_id が残らないように)
              directions: directions.map((d) => ({
                ...d,
                start_stop_id: undefined,
                end_stop_id: undefined,
              })),
            })
          }
        >
          <option value="">(選択してください)</option>
          {routes.map((r) => (
            <option key={r.route_id} value={r.route_id}>
              {r.route_id}
            </option>
          ))}
          {isUnknownRoute ? <option value={routeId}>{routeId} (マスタ未登録)</option> : null}
        </select>
      </label>

      {/* 判定モード */}
      <label>
        <span className="required">判定モード</span>
        <select
          aria-label={`接続線#${index + 1} 判定モード`}
          data-testid={`connecting-line-judgement-mode-${index}`}
          value={judgementMode}
          onChange={(e) => {
            const nextMode = e.target.value as ConnectingDelayJudgementMode;
            // モード切替時にモード固有項目をリセットする。
            // section_scheduled 以外なら方面ごとの区間駅は不要 (undefined に落とす)。
            const nextDirections =
              nextMode === 'section_scheduled'
                ? directions
                : directions.map((d) => ({
                    ...d,
                    start_stop_id: undefined,
                    end_stop_id: undefined,
                  }));
            emit({
              delay_judgement_mode: nextMode,
              directions: nextDirections,
            });
          }}
        >
          {CONNECTING_DELAY_MODE_META.map((m) => (
            <option key={m.mode} value={m.mode}>
              {m.label}
            </option>
          ))}
        </select>
      </label>
      <p className="hint" style={{ margin: '4px 0' }}>
        {judgementMode === 'whole_line'
          ? '接続線全体の最大遅延で判定します。方面ごとの区間指定は不要ですが、案内文面用の方面名 (下で入力) は必要です。'
          : judgementMode === 'section_scheduled'
            ? '指定区間に定刻在線している便の遅延で判定します。方面ごとに区間 (開始駅・終了駅) を指定してください。'
            : 'お客さまがまさに乗り換えようとしている接続列車の遅延で判定します。乗換時間 (秒) が必須です。'}
      </p>

      {/* 乗換時間 (transfer_aware のみ) */}
      {isTransferAwareMode ? (
        <label>
          <span className="required">乗換時間 (秒)</span>
          <input
            type="number"
            min={0}
            step={1}
            aria-label={`接続線#${index + 1} 乗換時間 (秒)`}
            data-testid={`connecting-line-transfer-time-${index}`}
            value={transferTimeSeconds}
            onChange={(e) =>
              emit({
                transfer_time_seconds: Math.max(0, Math.floor(Number(e.target.value))),
              })
            }
          />
        </label>
      ) : null}

      {/* 方面ブロック (1〜2件) */}
      <ConnectingDirectionsSection
        lineIndex={index}
        routeId={routeId}
        judgementMode={judgementMode}
        routes={routes}
        routeStops={routeStops}
        directions={directions}
        onChangeDirections={(nextDirections) => emit({ directions: nextDirections })}
      />

      {/* 接続線ブロック SSML テンプレート */}
      <label>
        <span className="required">接続線ブロック SSML テンプレート</span>
        <input
          type="text"
          aria-label={`接続線#${index + 1} ブロック SSML テンプレート`}
          data-testid={`connecting-line-block-template-${index}`}
          required
          value={lineTemplate}
          placeholder={CONNECTING_STATION_LINE_DEFAULT_SSML}
          onChange={(e) => emit({ ssml_line_template: e.target.value })}
        />
      </label>
      <PlaceholderLegend
        testId={`connecting-line-block-template-hint-${index}`}
        items={CONNECTING_STATION_LINE_PLACEHOLDERS}
      />

      {/* 遅延放送パターン (方面部分文の SSML) */}
      <ConnectingStationLineDelayPatternsTable
        lineIndex={index}
        patterns={patterns}
        onChange={(nextPatterns) => emit({ delay_patterns: nextPatterns })}
      />
    </div>
  );
}

/**
 * 接続線遅延 (乗換駅ベース / part2) の 1 接続線ブロック内の「方面リスト」セクション。
 *
 * 方面は 1〜2 件に制限し、＋ボタン (最大2件)・−ボタン (最後の1件は残す)・↑↓ ボタン
 * (順序変更) を提供する。方面ごとの詳細入力は `ConnectingDirectionBlock` に委譲する。
 */
function ConnectingDirectionsSection({
  lineIndex,
  routeId,
  judgementMode,
  routes,
  routeStops,
  directions,
  onChangeDirections,
}: {
  /** 親接続線ブロックの index (data-testid 用) */
  lineIndex: number;
  /** 親接続線の route_id (方面の direction_id / 区間駅の選択肢に使う) */
  routeId: string;
  /** 親接続線の判定モード (項目表示切替に使う) */
  judgementMode: ConnectingDelayJudgementMode;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  directions: ConnectingDirectionEntry[];
  onChangeDirections: (next: ConnectingDirectionEntry[]) => void;
}): React.JSX.Element {
  /** index 番目の方面を更新 */
  const updateDirection = (index: number, next: ConnectingDirectionEntry): void => {
    const nextDirections = directions.slice();
    nextDirections[index] = next;
    onChangeDirections(nextDirections);
  };
  /** 方面を末尾に追加 (最大 2 件まで) */
  const addDirection = (): void => {
    if (directions.length >= 2) return;
    onChangeDirections([...directions, createDefaultConnectingStationDirection()]);
  };
  /** 方面を削除 (最後の 1 件は残す) */
  const removeDirection = (index: number): void => {
    if (directions.length <= 1) return;
    const nextDirections = directions.slice();
    nextDirections.splice(index, 1);
    onChangeDirections(nextDirections);
  };
  /** 方面を上へ移動 */
  const moveUp = (index: number): void => {
    if (index <= 0) return;
    const nextDirections = directions.slice();
    const [moved] = nextDirections.splice(index, 1);
    if (!moved) return;
    nextDirections.splice(index - 1, 0, moved);
    onChangeDirections(nextDirections);
  };
  /** 方面を下へ移動 */
  const moveDown = (index: number): void => {
    if (index >= directions.length - 1) return;
    const nextDirections = directions.slice();
    const [moved] = nextDirections.splice(index, 1);
    if (!moved) return;
    nextDirections.splice(index + 1, 0, moved);
    onChangeDirections(nextDirections);
  };

  const canAdd = directions.length < 2;
  const canRemove = directions.length > 1;

  return (
    <div
      className="connecting-directions-section"
      data-testid={`connecting-directions-section-${lineIndex}`}
      style={{ marginTop: 8 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <strong>方面 ({directions.length}/2 件)</strong>
        {canAdd ? (
          <button
            type="button"
            data-testid={`connecting-direction-add-${lineIndex}`}
            aria-label={`接続線#${lineIndex + 1} に方面を追加`}
            onClick={addDirection}
          >
            ＋ 方面を追加
          </button>
        ) : (
          <span className="hint">最大 2 件まで追加できます</span>
        )}
      </div>
      <ol
        className="connecting-directions-list"
        style={{ listStyle: 'none', padding: 0, margin: 0 }}
      >
        {directions.map((direction, di) => (
          <li
            key={di}
            className="connecting-direction-item"
            data-testid={`connecting-direction-item-${lineIndex}-${di}`}
            style={{
              border: '1px dashed #ccc',
              borderRadius: 4,
              padding: '6px 10px',
              marginTop: 6,
            }}
          >
            <ConnectingDirectionBlock
              lineIndex={lineIndex}
              directionIndex={di}
              direction={direction}
              routeId={routeId}
              judgementMode={judgementMode}
              routes={routes}
              routeStops={routeStops}
              canRemove={canRemove}
              isFirst={di === 0}
              isLast={di === directions.length - 1}
              onChange={(next) => updateDirection(di, next)}
              onRemove={() => removeDirection(di)}
              onMoveUp={() => moveUp(di)}
              onMoveDown={() => moveDown(di)}
            />
          </li>
        ))}
      </ol>
    </div>
  );
}
